import { useRef, useState } from "react";
import { Button, Input, HStack } from "@postenbring/hedwig-react";
import { loadScript } from "/lib/load-script";

export const ChatbotInputField = ({
  placeholder,
  label,
  sendButtonText,
  inputAriaLabel,
  errorMessage,
  triggerDialogue,
}) => {
  const inputRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const [kindlyLoaded, setKindlyLoaded] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      initiateChat();
    }
  };

  const messageIsValid = () => {
    const message = inputRef.current?.value?.trim();
    return message && message.length >= 2;
  };

  // Helper function to clean up event listeners
  const cleanupEventListeners = () => {
    document.removeEventListener("kindly:message", kindlyMessageHandler);
    document.removeEventListener("kindly:ui", kindlyUiHandler);
    document.removeEventListener("kindly:load", kindlyLoadHandler);
  };

  /**
   * The flow here is as follows:
   * 1. Submit or Enter fires initiateChat().
   * 2. Sets up event listeners for chat messages and UI events.
   * 3. Load the Kindly chat script.
   * 4. This, in turn, will fire the events, 1 `kindly:load`, 2 `kindly:ui` and 3 `kindly:message`.
   */

  /**
   * Event handler #3
   * Send the user's message to Kindly chat
   */
  const kindlyMessageHandler = (event) => {
    if (event.detail.newMessage.exchange_id === triggerDialogue) {
      window.kindlyChat.sendMessage(inputRef.current.value.trim(), {
        sender: "user",
        newContext: {
          text_field_input: "Opened from textfield",
        },
      });
      inputRef.current.value = "";
      return;
    }
  };

  /**
   * Event handler #2
   * Trigger the dialogue when the chat bubble is opened
   */
  const kindlyUiHandler = (event) => {
    if (event.detail.type === "open-chatbubble" && event.detail.chatId === null) {
      window.kindlyChat.triggerDialogue(triggerDialogue);
      document.removeEventListener("kindly:ui", kindlyUiHandler);
    }
  };

  /**
   * Event handler #1
   * Open the chat dialog when loading is done
   */
  const kindlyLoadHandler = async () => {
    setKindlyLoaded(true);
    window.kindlyChat.openChat();
    document.removeEventListener("kindly:load", kindlyLoadHandler);
  };

  const initiateChat = () => {
    if (!messageIsValid()) {
      setHasError(true);
      return;
    }

    setHasError(false);

    // Clean up any previous listeners
    cleanupEventListeners();

    if (kindlyLoaded) {
      // Chat already loaded - send message directly (no events needed)
      window.kindlyChat.sendMessage(inputRef.current.value.trim(), {
        sender: "user",
        newContext: {
          text_field_input: "Opened from textfield",
        },
      });
      inputRef.current.value = "";
    } else {
      // Add event listeners for chat messages and UI events
      document.addEventListener("kindly:message", kindlyMessageHandler);
      document.addEventListener("kindly:ui", kindlyUiHandler);
      document.addEventListener("kindly:load", kindlyLoadHandler);

      // Load the Kindly chat script
      loadScript(
        "https://chat.kindlycdn.com/kindly-chat.js",
        "kindly-chat",
        kindlyScriptAttributes,
      ).catch(function (error) {
        setHasError(true);
        console.error("Error loading script:", error);
        cleanupEventListeners();
      });
    }
  };

  return (
    <HStack gap="8" wrap>
      <Input
        type="text"
        ref={inputRef}
        errorMessage={hasError ? errorMessage : ""}
        aria-label={inputAriaLabel ?? "Type your question here"}
        label={label ?? placeholder}
        labelProps={{
          style: { display: "none" },
          htmlFor: "chat-input-text",
        }}
        id="chat-input-text"
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        style={{ flexGrow: 2 }}
      />
      <Button onClick={initiateChat} style={{ whiteSpace: "nowrap" }}>
        {sendButtonText ?? "Send"}
      </Button>
    </HStack>
  );
};
